import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiLayers, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const FloorManagement = () => {
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFloor, setEditingFloor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchFloors();
  }, []);

  const fetchFloors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/vendor/room-management/floors');
      setFloors(response.data);
    } catch (error) {
      console.error('Error fetching floors:', error);
      toast.error('Failed to fetch floors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFloor) {
        await api.put(`/vendor/room-management/floors/${editingFloor._id}`, formData);
        toast.success('Floor updated successfully');
        setShowEditModal(false);
      } else {
        await api.post('/vendor/room-management/floors', formData);
        toast.success('Floor created successfully');
        setShowAddModal(false);
      }
      setFormData({ name: '', description: '' });
      setEditingFloor(null);
      fetchFloors();
    } catch (error) {
      console.error('Error saving floor:', error);
      toast.error(error.response?.data?.message || 'Failed to save floor');
    }
  };

  const handleEdit = (floor) => {
    setEditingFloor(floor);
    setFormData({
      name: floor.name,
      description: floor.description || ''
    });
    setShowEditModal(true);
  };

  const handleDelete = async (floorId) => {
    if (!window.confirm('Are you sure you want to delete this floor?')) return;
    
    try {
      await api.delete(`/vendor/room-management/floors/${floorId}`);
      toast.success('Floor deleted successfully');
      fetchFloors();
    } catch (error) {
      console.error('Error deleting floor:', error);
      toast.error(error.response?.data?.message || 'Failed to delete floor');
    }
  };

  const moveFloor = async (floorId, direction) => {
    try {
      await api.put(`/vendor/room-management/floors/${floorId}`, {
        sortOrder: direction === 'up' ? -1 : 1
      });
      fetchFloors();
    } catch (error) {
      console.error('Error moving floor:', error);
      toast.error('Failed to reorder floor');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setEditingFloor(null);
    setShowAddModal(false);
    setShowEditModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 sm:h-64">
        <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Floor Management</h2>
          <p className="text-gray-600 text-sm sm:text-base">Create and manage building floors</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors text-sm sm:text-base w-full sm:w-auto"
        >
          <FiPlus className="mr-2 w-4 h-4" />
          <span>Add Floor</span>
        </button>
      </div>

      {/* Floors List */}
      <div className="bg-white rounded-lg shadow">
        {floors.length === 0 ? (
          <div className="p-6 sm:p-8 text-center">
            <FiLayers className="mx-auto w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No floors found</h3>
            <p className="text-gray-500 text-sm sm:text-base mb-4">Get started by creating your first floor</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
            >
              Create Floor
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Floor Name
                  </th>
                  <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {floors.map((floor, index) => (
                  <tr key={floor._id} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => moveFloor(floor._id, 'up')}
                          disabled={index === 0}
                          className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FiChevronUp className="text-gray-600 w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => moveFloor(floor._id, 'down')}
                          disabled={index === floors.length - 1}
                          className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FiChevronDown className="text-gray-600 w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <span className="text-sm text-gray-900">{index + 1}</span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FiLayers className="text-blue-600 mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-sm font-medium text-gray-900">{floor.name}</span>
                      </div>
                      {/* Mobile-only additional info */}
                      <div className="sm:hidden mt-1 space-y-1">
                        {floor.description && (
                          <div className="text-xs text-gray-600 truncate max-w-[150px]">{floor.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {floor.description || 'No description'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        floor.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {floor.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => handleEdit(floor)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(floor._id)}
                          className="text-red-600 hover:text-red-900 p-1"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Floor Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 sm:p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg sm:text-xl font-bold">Add New Floor</h3>
                <button
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={resetForm}
                >
                  <span className="text-xl sm:text-2xl">×</span>
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="p-4 sm:p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Floor Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="e.g., Ground Floor, First Floor"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    rows="3"
                    placeholder="Optional description"
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Add Floor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Floor Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 sm:p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg sm:text-xl font-bold">Edit Floor</h3>
                <button
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors duration-200"
                  onClick={resetForm}
                >
                  <span className="text-xl sm:text-2xl">×</span>
                </button>
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="p-4 sm:p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Floor Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="e.g., Ground Floor, First Floor"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    rows="3"
                    placeholder="Optional description"
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Update Floor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloorManagement;
